// Ce qui se joue : la musique et les bruits de fond de la carte, la musique du combat en cours (la carte reprend où
// elle en était à la fin), et l'ambiance de la météo pendant une averse, plus ou moins forte comme elle.
import { gameConfig } from '../../shared/game-config.ts';
import type { BattleMode, MapView } from '../../shared/protocol.ts';
import type { Sound } from '../../shared/sounds.ts';
import { weatherStrength } from '../../shared/weather.ts';
import { LoopDeck } from './loop-deck.ts';
import type { SoundBoard } from './sound.ts';

/** Bruits de fond pendant un combat : en retrait de la musique. */
const BATTLE_AMBIENCE = 0.35;
/** Intervalle de suivi de la météo (ms) : une averse monte et retombe en une demi-heure du jeu. */
const WEATHER_TICK_MS = 2000;

export class Soundtrack {
  private readonly music: LoopDeck;
  private readonly ambience: LoopDeck;
  private readonly weather: LoopDeck;
  private catalog = new Map<number, Sound>();
  private map: MapView | null = null;
  private battle: BattleMode | null = null;
  private timer: ReturnType<typeof setInterval> | undefined;

  constructor(sounds: SoundBoard) {
    this.music = new LoopDeck(sounds, 'music');
    this.ambience = new LoopDeck(sounds, 'ambience');
    this.weather = new LoopDeck(sounds, 'ambience');
  }

  setCatalog(sounds: readonly Sound[]): void {
    this.catalog = new Map(sounds.map((sound) => [sound.id, sound]));
    this.refresh();
  }

  /** Carte où se trouve le joueur (arrivée, ou modifiée dans l'administration). */
  setMap(map: MapView): void {
    this.map = map;
    this.refresh();
    clearInterval(this.timer);
    this.timer = map.weather.kind === 'none' ? undefined : setInterval(() => this.refreshWeather(), WEATHER_TICK_MS);
  }

  startBattle(mode: BattleMode): void {
    this.battle = mode;
    this.refresh();
  }

  endBattle(): void {
    if (this.battle === null) return;
    this.battle = null;
    this.refresh();
  }

  /** Configuration du jeu changée (sons des combats, de la météo). */
  refresh(): void {
    const map = this.map;
    this.music.set(this.battleMusic() ?? this.sound(map?.music ?? null));
    this.ambience.set(this.sound(map?.ambience ?? null), this.battle ? BATTLE_AMBIENCE : 1);
    this.refreshWeather();
  }

  /** Déconnexion : tout se tait. */
  stop(): void {
    clearInterval(this.timer);
    this.timer = undefined;
    this.map = null;
    this.battle = null;
    this.music.clear();
    this.ambience.clear();
    this.weather.clear();
  }

  private refreshWeather(): void {
    const weather = this.map?.weather;
    if (!this.map || !weather || weather.kind === 'none') return this.weather.set(null);
    const strength = weatherStrength(weather, this.map.id, gameConfig().dayNight, Date.now());
    const level = strength * (0.4 + 0.6 * weather.intensity) * (this.battle ? BATTLE_AMBIENCE : 1);
    this.weather.set(this.sound(gameConfig().audio.weather[weather.kind]), level);
  }

  /** Musique du combat en cours, si la configuration en donne une (sinon celle de la carte continue). */
  private battleMusic(): Sound | null {
    const { audio } = gameConfig();
    switch (this.battle) {
      case null: return null;
      case 'wild': return this.sound(audio.wildBattleMusic);
      case 'trainer': return this.sound(audio.trainerBattleMusic);
      case 'duel': return this.sound(audio.duelMusic ?? audio.trainerBattleMusic);
    }
  }

  private sound(id: number | null): Sound | null {
    return id === null ? null : (this.catalog.get(id) ?? null);
  }
}
